import { PencilSimpleIcon, TrashIcon, UserIcon } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetPropertyManagers } from '@/api/property-managers'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DetailsListItem } from '@/components/ui/details-list'
import { EmailLink } from '@/components/ui/email-link'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { PhoneLink } from '@/components/ui/phone-link'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory } from '@/factories'
import { formatDateOnly } from '@/utils/date'

import { DetailsSection } from '../../shared/details-section'
import { CountedTitleSkeleton, DetailsModalSkeleton } from '../../shared/details-skeleton'
import { PersonProfile } from '../../shared/person-profile'
import { DeletePropertyManagerConfirmationPopup } from '../delete-property-manager-confirmation-popup'
import { PropertyManagerFormModal } from '../property-manager-form-modal'

interface PropertyManagerDetailsModalProps {
  onClose: VoidFunction
  propertyManagerId: number
}

export const PropertyManagerDetailsModal = ({
  onClose,
  propertyManagerId,
}: PropertyManagerDetailsModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { data, isLoading } = useGetPropertyManagers()

  const propertyManager = data?.find((item) => item.id === propertyManagerId)
  const isRemoved = !isLoading && !!data && !propertyManager

  // Note: closes once the record is gone from the list (e.g. deleted from the popup stacked on top)
  useEffect(() => {
    if (isRemoved) onClose()
  }, [isRemoved, onClose])

  const handleEdit = () => {
    if (!propertyManager) return
    popupFactory.open(
      PropertyManagerFormModal,
      { mode: 'edit', propertyManager },
      { suspendCurrent: true },
    )
  }

  const handleDelete = () => {
    if (!propertyManager) return
    popupFactory.open(
      DeletePropertyManagerConfirmationPopup,
      { propertyManager },
      { suspendCurrent: true },
    )
  }

  const footer = (
    <>
      <Button use="secondary" onClick={onClose}>
        {t('common:actions.close')}
      </Button>
      <Button disabled={!propertyManager} use="secondary" onClick={handleDelete}>
        <Icon icon={TrashIcon} color="red500" />
        {t('common:actions.delete')}
      </Button>
      <Button disabled={!propertyManager} onClick={handleEdit}>
        {t('common:actions.edit')}
        <Icon icon={PencilSimpleIcon} />
      </Button>
    </>
  )

  return (
    <Modal
      title={t('organisation:propertyManagers.detailsModal.title')}
      headerIcon={UserIcon}
      footer={footer}
      onClose={onClose}
    >
      {isLoading || !propertyManager ? (
        <DetailsModalSkeleton
          sections={[
            {
              labels: [
                t('organisation:propertyManagers.detailsModal.email'),
                t('organisation:propertyManagers.detailsModal.phone'),
                t('organisation:propertyManagers.detailsModal.jobTitle'),
              ],
              title: t('organisation:propertyManagers.detailsModal.contact'),
            },
            {
              badgeRow: 0,
              labels: [
                t('organisation:propertyManagers.detailsModal.status'),
                t('organisation:propertyManagers.detailsModal.createdAt'),
              ],
              title: t('organisation:propertyManagers.detailsModal.account'),
            },
            {
              rows: 1,
              title: (
                <CountedTitleSkeleton
                  format={(count) =>
                    t('organisation:propertyManagers.detailsModal.buildings', { count })
                  }
                />
              ),
            },
          ]}
        />
      ) : (
        <Flex direction="column" gap={20}>
          <PersonProfile subtitle={propertyManager.jobTitle} user={propertyManager.user} />

          <DetailsSection title={t('organisation:propertyManagers.detailsModal.contact')}>
            <DetailsListItem label={t('organisation:propertyManagers.detailsModal.email')}>
              <EmailLink email={propertyManager.user.email} />
            </DetailsListItem>
            <DetailsListItem label={t('organisation:propertyManagers.detailsModal.phone')}>
              <PhoneLink phone={propertyManager.phone} />
            </DetailsListItem>
            <DetailsListItem label={t('organisation:propertyManagers.detailsModal.jobTitle')}>
              {propertyManager.jobTitle || EMPTY_VALUE}
            </DetailsListItem>
          </DetailsSection>

          <DetailsSection title={t('organisation:propertyManagers.detailsModal.account')}>
            <DetailsListItem label={t('organisation:propertyManagers.detailsModal.status')}>
              <Badge
                color={propertyManager.user.isActive ? 'green' : 'neutral'}
                size="small"
                variant="light"
              >
                {propertyManager.user.isActive
                  ? t('organisation:propertyManagers.detailsModal.active')
                  : t('organisation:propertyManagers.detailsModal.inactive')}
              </Badge>
            </DetailsListItem>
            <DetailsListItem label={t('organisation:propertyManagers.detailsModal.createdAt')}>
              {formatDateOnly(new Date(propertyManager.createdAt))}
            </DetailsListItem>
          </DetailsSection>

          <DetailsSection
            title={t('organisation:propertyManagers.detailsModal.buildings', {
              count: propertyManager.buildingIds.length,
            })}
          >
            {propertyManager.buildingIds.length ? (
              propertyManager.buildingIds.map((building) => (
                <DetailsListItem key={building.id}>{building.name}</DetailsListItem>
              ))
            ) : (
              <DetailsListItem
                label={t('organisation:propertyManagers.detailsModal.noBuildings')}
              />
            )}
          </DetailsSection>
        </Flex>
      )}
    </Modal>
  )
}
